import { safeErrorLogAttributes } from "@afar-research/client-runtime/errors";
import type { EnvironmentId } from "@afar-research/contracts";
import * as Effect from "effect/Effect";

import { awaitPreparedConnection } from "~/state/session";

/**
 * Why a panel's view socket could not connect. The Environment answers a refused upgrade with
 * nothing the panel can show, so the reason is logged here, once per run of failures: a panel
 * retries every second and would otherwise log as often.
 */
const STILL_WAITING_AFTER_MS = 15_000;

export interface ViewSocketRefusals {
  readonly warn: (reason: string, details?: Record<string, unknown>) => void;
  /** The socket opened; the next failure starts a new run and is logged again. */
  readonly reset: () => void;
}

export function viewSocketRefusals(path: string): ViewSocketRefusals {
  let logged = false;
  return {
    warn: (reason, details) => {
      if (logged) return;
      logged = true;
      console.warn(`View socket refused: ${reason}`, { path, ...details });
    },
    reset: () => {
      logged = false;
    },
  };
}

/**
 * An authorized URL for one of a Cloud Workspace's view sockets (ADR-0137): the Collaborative
 * Browser's or the Cloud Display's. It waits until the Environment is connected, so a panel
 * opened before the connection is ready still connects, and resolves to null when the
 * connection cannot open another socket or refuses a ticket.
 */
export async function authorizeViewSocket(
  environmentId: EnvironmentId,
  path: string,
  signal: AbortSignal,
  refusals: ViewSocketRefusals,
): Promise<string | null> {
  // Waiting is right while the Environment connects, but a wait that never ends is the one
  // failure with nothing else to report it.
  const stillWaiting = setTimeout(
    () => console.warn("View socket waiting: the Environment has not connected yet.", { path }),
    STILL_WAITING_AFTER_MS,
  );
  const prepared = await awaitPreparedConnection(environmentId, signal).finally(() =>
    clearTimeout(stillWaiting),
  );
  if (prepared.authorizeSocket === undefined) {
    refusals.warn("this connection cannot open another socket.", {
      target: prepared.target._tag,
    });
    return null;
  }
  return Effect.runPromise(
    prepared.authorizeSocket(path).pipe(
      Effect.catch((error) =>
        Effect.sync(() => {
          refusals.warn("the ticket was refused.", { ...safeErrorLogAttributes(error) });
          return null;
        }),
      ),
    ),
    { signal },
  );
}
